import { Wallet, Award, FolderGit2, Users, CheckSquare } from 'lucide-react';
import { motion } from 'motion/react';
import MetricCard from '../MetricCard';
import { DashboardData } from '../../types/dashboard';

interface HomeKPIsProps {
  isLoading?: boolean;
  dashboardData: DashboardData | null | undefined;
  projects?: any[];
  tasks?: any[];
  finance?: any[];
  companies?: any[];
  setCurrentView: (view: any) => void;
}

function formatBRL(value: number): string {
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}k`;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export default function HomeKPIs({ isLoading, dashboardData, setCurrentView }: HomeKPIsProps) {
  const fin: DashboardData['financial'] = dashboardData?.financial || ({} as any);
  const proj: DashboardData['projects'] = dashboardData?.projects || ({} as any);
  const cust: DashboardData['customers'] = dashboardData?.customers || ({} as any);
  const tsks: DashboardData['tasks'] = dashboardData?.tasks || ({} as any);

  const revenueTotal = fin.revenue?.total ?? 0;
  const revenueValue = revenueTotal >= 1000 ? `R$ ${(revenueTotal / 1000).toFixed(1)}k` : formatBRL(revenueTotal);
  const revenueCount = fin.revenue?.count || 0;
  const revenueAvg = revenueCount > 0 ? formatBRL(revenueTotal / revenueCount) : 'R$ 0';

  // 2. Fluxo de Caixa
  const cashBalance = fin.cashFlow?.balance ?? 0;
  const cashValue = cashBalance >= 1000 || cashBalance <= -1000 ? `R$ ${(cashBalance / 1000).toFixed(1)}k` : formatBRL(cashBalance);
  const cashRatio = fin.cashFlow?.ratio ?? 1;
  const cashStable = cashBalance >= 0;

  // 3. Projetos em Andamento
  const activeProjectsCount = proj.active ?? 0;
  const plannedProjectsCount = proj.planned ?? 0;
  const inProgressCount = activeProjectsCount + plannedProjectsCount;
  const overdueProjectsCount = proj.overdue ?? 0;
  const projectOnTimeRate = proj.onTimeRate ?? (inProgressCount > 0 ? Math.round(((inProgressCount - overdueProjectsCount) / inProgressCount) * 100) : 100);
  const projTotal = proj.total ?? 0;

  // 4. Clientes Ativos
  const activeClientsCount = cust.active ?? 0;
  const newClientsThisMonth = cust.newThisMonth ?? 0;
  const custTotal = cust.total ?? 0;
  const retRate = custTotal > 0 ? Math.round((activeClientsCount / custTotal) * 100) : 0;

  // 5. Tarefas Pendentes
  const pendingTasksCount = tsks.pending ?? 0;
  const overdueTasksCount = tsks.overdue ?? 0;
  const totalTasksCount = tsks.total ?? 0;
  const taskCompletionRate = tsks.completionRate ?? (totalTasksCount > 0 ? Math.round((tsks.completed || 0 / Math.max(1, totalTasksCount)) * 100) : 0);

  // 6. Score CYZOR
  const scores = dashboardData?.scores;
  let cyzorScore = scores?.overallScore ?? 0;
  let cyzorStatus: string;
  if (cyzorScore === 0) cyzorStatus = 'Sem dados';
  else if (cyzorScore < 75) cyzorStatus = 'Requer atenção';
  else if (cyzorScore < 90) cyzorStatus = 'Saudável';
  else cyzorStatus = 'Excelente';

  const IconFolder = FolderGit2 as any;
  const IconCard = CheckSquare as any;
  const IconUser = Users as any;
  const IconWallet = Wallet as any;
  const IconAward = Award as any;

  const cards: any[] = [
    {
      label: 'Receita do Mês',
      value: revenueValue,
      trend: revenueCount > 1 ? 'Consolidado' : 'Sem histórico',
      comparison: `${revenueCount} entradas registradas`,
      secondaryLabel: 'Média por entrada',
      secondaryValue: revenueAvg,
      progress: revenueTotal > 0 ? Math.min(100, Math.round((revenueTotal / 200000) * 100)) : 0,
      progressColor: 'bg-emerald-600',
      color: 'text-emerald-700',
      bg: 'bg-emerald-50',
      icon: IconWallet,
      view: 'financeiro',
    },
    {
      label: 'Fluxo de Caixa',
      value: cashValue,
      trend: cashStable ? 'Positivo' : 'Déficit',
      comparison: `Balanço atual (${formatBRL(cashBalance)})`,
      secondaryLabel: 'Saúde Financeira',
      secondaryValue: cashStable ? 'Estável' : 'Atenção',
      progress: Math.max(0, Math.min(100, cashStable ? Math.round(Math.min(cashRatio, 2) * 50) : Math.max(0, 50 - Math.abs(cashRatio - 1) * 50))),
      progressColor: cashStable ? 'bg-indigo-600' : 'bg-rose-600',
      color: cashStable ? 'text-indigo-700' : 'text-rose-700',
      bg: cashStable ? 'bg-indigo-50' : 'bg-rose-50',
      icon: IconWallet,
      view: 'financeiro',
    },
    {
      label: 'Projetos em Andamento',
      value: `${inProgressCount} ativos`,
      trend: overdueProjectsCount > 0 ? `${overdueProjectsCount} em risco` : 'Dentro do prazo',
      comparison: `${projTotal} projetos registrados`,
      secondaryLabel: 'No prazo',
      secondaryValue: `${projectOnTimeRate}%`,
      progress: projectOnTimeRate,
      progressColor: projectOnTimeRate >= 80 ? 'bg-blue-600' : 'bg-amber-600',
      color: 'text-blue-700',
      bg: 'bg-blue-50',
      icon: IconFolder,
      view: 'projetos',
    },
    {
      label: 'Clientes Ativos',
      value: `${activeClientsCount} ativos`,
      trend: `+${newClientsThisMonth} novos`,
      comparison: `${custTotal} cadastrados`,
      secondaryLabel: 'Taxa de retenção',
      secondaryValue: `${retRate}%`,
      progress: retRate,
      progressColor: 'bg-purple-600',
      color: 'text-purple-700',
      bg: 'bg-purple-50',
      icon: IconUser,
      view: 'empresas',
    },
    {
      label: 'Tarefas Pendentes',
      value: `${pendingTasksCount} pendentes`,
      trend: `${overdueTasksCount} críticas`,
      comparison: `${totalTasksCount} tarefas no total`,
      secondaryLabel: 'Concluídas',
      secondaryValue: `${taskCompletionRate}%`,
      progress: taskCompletionRate,
      progressColor: 'bg-amber-600',
      color: 'text-amber-700',
      bg: 'bg-amber-50',
      icon: IconCard,
      view: 'projetos',
    },
    {
      label: 'Score CYZOR',
      value: cyzorScore > 0 ? `${cyzorScore}%` : 'N/A',
      trend: cyzorScore > 0 ? (cyzorScore >= 75 ? 'No alvo' : 'Abaixo da meta') : 'Sem dados',
      comparison: 'Saúde da Empresa',
      secondaryLabel: 'Status Geral',
      secondaryValue: cyzorStatus,
      progress: cyzorScore || 0,
      progressColor: cyzorScore >= 90 ? 'bg-indigo-600' : cyzorScore >= 75 ? 'bg-blue-600' : 'bg-amber-600',
      color: 'text-cyan-700',
      bg: 'bg-cyan-50',
      icon: IconAward,
      view: 'ia',
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between px-1">
        <div className="flex flex-col">
          <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Cyzor Intelligence</span>
          <h2 className="text-sm font-display font-black text-white tracking-tight">Centro de Metrricas Globais</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-xl border border-white/10 shadow-2xs backdrop-blur-md">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
            <span className="text-[9px] font-bold text-white uppercase tracking-wider">Tempo Real</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
        {cards.map((card, i) => (
          <MetricCard
            key={i}
            isLoading={isLoading || (!dashboardData && isLoading !== false)}
            title={card.label}
            value={card.value}
            icon={card.icon}
            trend={
              card.trend
                ? {
                    value: card.trend,
                    type: card.label === 'Fluxo de Caixa' && !cashStable ? 'down' : 'up',
                    label: card.trend,
                  }
                : undefined
            }
            subtext={card.comparison}
            secondaryLabel={card.secondaryLabel}
            secondaryValue={card.secondaryValue}
            progress={card.progress}
            progressColor={card.progressColor}
            color={card.color}
            bg={card.bg}
            onClick={() => setCurrentView(card.view)}
          />
        ))}
      </div>
    </div>
  );
}
